/**
 * Service d'audit pour tracer les actions administratives
 * Enregistre dans admin_audit sans jamais bloquer l'UI
 */

import { supabase } from '@/lib/supabaseClient';

interface AuditMeta {
  [key: string]: unknown;
}

/**
 * Enregistre une action admin dans la table d'audit
 * 
 * @param action - Type d'action (VALIDATE, REJECT, PIPELINE, etc.)
 * @param entity - Type d'entité (lead, ticket, profile)
 * @param entityId - ID de l'entité modifiée
 * @param meta - Métadonnées additionnelles (optionnel)
 * 
 * Erreurs silencieuses: ne jamais casser l'UI si l'audit échoue
 */
export async function logAdmin(
  action: string,
  entity: string,
  entityId: number | string,
  meta?: AuditMeta
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.warn('[AUDIT] Aucun utilisateur connecté, log ignoré');
      return;
    }

    const { error } = await supabase
      .from('admin_audit')
      .insert({
        admin_user_id: user.id,
        action,
        entity,
        entity_id: String(entityId),
        meta: meta || null,
      });

    if (error) {
      console.warn('[AUDIT] Erreur lors de l\'enregistrement:', error.message);
      // Ne pas throw: l'audit ne doit jamais bloquer l'UI
      return;
    }

    console.log(`[AUDIT] ${action} sur ${entity}#${entityId}`, meta);
  } catch (err) {
    // Catch silencieux: l'audit est optionnel
    console.warn('[AUDIT] Exception capturée:', err);
  }
}

/**
 * Liste les logs d'audit avec filtres optionnels
 * 
 * @param filters - Filtres optionnels (q, entity, action, limit)
 * @returns Liste des logs d'audit
 */
export async function listAudit(filters?: {
  q?: string;
  entity?: string;
  action?: string;
  limit?: number;
}): Promise<Array<{
  id: number;
  adminUserId: string | null;
  action: string;
  entity: string;
  entityId: string;
  meta: unknown;
  createdAt: string;
}>> {
  try {
    let query = supabase
      .from('admin_audit')
      .select('id, admin_user_id, action, entity, entity_id, meta, created_at')
      .order('created_at', { ascending: false })
      .limit(filters?.limit || 200);

    // Filtrer par entity si fourni
    if (filters?.entity && filters.entity !== 'ALL') {
      query = query.eq('entity', filters.entity);
    }

    // Filtrer par action si fourni
    if (filters?.action && filters.action !== 'ALL') {
      query = query.eq('action', filters.action);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Erreur lors de la récupération des logs: ${error.message}`);
    }

    let results = (data || []).map((log) => ({
      id: log.id,
      adminUserId: log.admin_user_id,
      action: log.action,
      entity: log.entity,
      entityId: log.entity_id,
      meta: log.meta,
      createdAt: log.created_at,
    }));

    // Filtrer par recherche textuelle (front-end)
    if (filters?.q && filters.q.trim()) {
      const searchLower = filters.q.toLowerCase();
      results = results.filter((log) => {
        return (
          log.action.toLowerCase().includes(searchLower) ||
          log.entity.toLowerCase().includes(searchLower) ||
          log.entityId.toLowerCase().includes(searchLower)
        );
      });
    }

    return results;
  } catch (err) {
    throw new Error(
      `Impossible de charger les logs d'audit: ${err instanceof Error ? err.message : 'Erreur inconnue'}`
    );
  }
}